/**
 * 渐变工具（补齐 registry.ts 早先登记的占位工具 / DESIGN.md §6.2）。
 *
 * 交互：按下定起点，拖拽定终点（Photoshop 惯例——渐变轴就是这条拖拽线），
 * 松手提交（一条历史）。取色/裁剪逻辑在 app/gradient-fill.ts。
 *
 * 色标不是 ToolManager 能记的静态值（`ToolOptionValue` 只有
 * number/boolean/string），所以自定义渐变色标由装配层（main.tsx）另开
 * 一个信号管着、经 `deps.stops()` 注入——省略/返回 null 时退回默认的
 * 前景→背景（向后兼容，也是没打开过渐变编辑器时的样子）。
 */
import { fillGradientDocument, type GradientPoint, type GradientStopInput } from '../../app/gradient-fill';
import type { AppStore } from '../../app/store';
import { sampleGradient, type GradientKind, type GradientSpec, type GradientStop } from '../../engine/fill/gradient';
import type { SeparableBlendMode } from '../../gpu/shaders/blend/modes';
import type { ToolManager } from './registry';

/**
 * 混合模式→Canvas 原生合成操作：拖拽预览用（真正落盘走 applyFill 的精确
 * 公式，这里只是让预览"看起来对"，两边允许有细微数值差异）。
 */
const COMPOSITE_OPERATION: Readonly<Record<SeparableBlendMode, GlobalCompositeOperation>> = {
  normal: 'source-over',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  colorDodge: 'color-dodge',
  colorBurn: 'color-burn',
  hardLight: 'hard-light',
  softLight: 'soft-light',
  difference: 'difference',
  exclusion: 'exclusion',
  // Canvas2D 原生合成操作没有这 9 种，挑视觉上最接近的近似——预览本来就
  // 允许跟精确公式有细微差异（见上面的注释），"溶解"的抖动纹理这里更是
  // 完全没法用原生操作表达，只退回 normal 让预览至少不出怪颜色
  dissolve: 'source-over',
  linearBurn: 'color-burn',
  linearDodge: 'lighter', // 'lighter' 就是 canvas 原生的加法混合，恰好是 Add 的定义
  vividLight: 'hard-light',
  linearLight: 'hard-light',
  pinLight: 'hard-light',
  hardMix: 'hard-light',
  subtract: 'difference',
  divide: 'color-dodge',
};

/** 预览网格的最大边长（文档像素）——大文档降采样再靠 drawImage 双线性放大，开销跟文档大小脱钩。 */
const PREVIEW_MAX_DIM = 256;

export interface GradientToolDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
  /** 自定义渐变色标；null/省略 = 前景→背景。 */
  readonly stops?: (() => readonly GradientStopInput[] | null) | undefined;
}

export class GradientToolController {
  readonly #deps: GradientToolDeps;
  #start: GradientPoint | null = null;
  #current: GradientPoint | null = null;

  constructor(deps: GradientToolDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#start !== null;
  }

  handlePointer(phase: 'down' | 'move' | 'up' | 'double', point: GradientPoint): boolean {
    const { store, tools } = this.#deps;
    if (tools.active.id !== 'gradient') return false;
    if (store.document === null) return true;

    if (phase === 'down') {
      this.#start = point;
      this.#current = point;
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'move' && this.#start !== null) {
      this.#current = point;
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'up' && this.#start !== null) {
      const from = this.#start;
      const to = this.#current ?? point;
      this.#start = null;
      this.#current = null;
      const { kind, reverse, blendMode, opacity, dither, stops } = this.#readOptions();
      fillGradientDocument(store, from, to, {
        kind,
        reverse,
        blendMode,
        opacity,
        dither,
        ...(stops === null ? {} : { stops }),
      });
      this.#deps.requestRender();
      return true;
    }
    return true;
  }

  /** 属性栏当前的渐变选项——提交（up）和拖拽预览共用同一份读取逻辑。 */
  #readOptions(): {
    kind: GradientKind;
    reverse: boolean;
    blendMode: SeparableBlendMode;
    opacity: number;
    dither: boolean;
    stops: readonly GradientStopInput[] | null;
  } {
    const { tools } = this.#deps;
    return {
      kind: tools.getOption('gradient', 'type') as GradientKind,
      reverse: tools.getOption('gradient', 'reverse') as boolean,
      blendMode: tools.getOption('gradient', 'blendMode') as SeparableBlendMode,
      opacity: (tools.getOption('gradient', 'opacity') as number) / 100,
      dither: tools.getOption('gradient', 'dither') as boolean,
      stops: this.#deps.stops?.() ?? null,
    };
  }

  /**
   * 拖拽期间的实时预览：真实渐变颜色（跟落盘同一套 sampleGradient 公式，
   * 大文档降采样成一张小网格再用 Canvas 双线性放大，开销跟文档大小脱钩），
   * 叠加轴线+两端手柄——不然只有一条虚线，拖到哪、松手会长什么样完全靠猜，
   * 跟真实 Photoshop 拖拽时就能看见渐变实时铺开的手感差得远。
   */
  drawOverlay(ctx: CanvasRenderingContext2D): void {
    if (this.#start === null || this.#current === null) return;
    this.#drawGradientPreview(ctx, this.#start, this.#current);

    ctx.save();
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    for (const color of ['black', 'white'] as const) {
      ctx.strokeStyle = color;
      ctx.lineDashOffset = color === 'black' ? 0 : 4;
      ctx.beginPath();
      ctx.moveTo(this.#start.x, this.#start.y);
      ctx.lineTo(this.#current.x, this.#current.y);
      ctx.stroke();
    }
    for (const p of [this.#start, this.#current]) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
      ctx.fillStyle = 'white';
      ctx.fill();
      ctx.strokeStyle = 'black';
      ctx.setLineDash([]);
      ctx.stroke();
    }
    ctx.restore();
  }

  #drawGradientPreview(ctx: CanvasRenderingContext2D, from: GradientPoint, to: GradientPoint): void {
    if (from.x === to.x && from.y === to.y) return;
    const { store } = this.#deps;
    const doc = store.document;
    if (doc === null) return;

    const selection = store.selection;
    const bounds =
      selection !== null && !selection.isEmpty
        ? selection.contentBounds()
        : { x: 0, y: 0, width: doc.size.width, height: doc.size.height };
    if (bounds === null || bounds.width <= 0 || bounds.height <= 0) return;

    const { kind, reverse, blendMode, opacity, stops: stopsInput } = this.#readOptions();
    const stops: readonly GradientStop[] =
      stopsInput !== null
        ? stopsInput.map((s) => ({
            offset: s.offset,
            color: [s.color.r, s.color.g, s.color.b, Math.round(s.alpha * 255)] as const,
          }))
        : [
            { offset: 0, color: [store.foreground.r, store.foreground.g, store.foreground.b, 255] },
            { offset: 1, color: [store.background.r, store.background.g, store.background.b, 255] },
          ];
    const spec: GradientSpec = { kind, from, to, stops, space: 'linear', reverse };

    const scale = Math.min(1, PREVIEW_MAX_DIM / Math.max(bounds.width, bounds.height));
    const gw = Math.max(1, Math.round(bounds.width * scale));
    const gh = Math.max(1, Math.round(bounds.height * scale));

    const grid = document.createElement('canvas');
    grid.width = gw;
    grid.height = gh;
    const gridCtx = grid.getContext('2d');
    if (gridCtx === null) return;
    const image = gridCtx.createImageData(gw, gh);
    for (let gy = 0; gy < gh; gy++) {
      const y = bounds.y + ((gy + 0.5) / gh) * bounds.height;
      for (let gx = 0; gx < gw; gx++) {
        const x = bounds.x + ((gx + 0.5) / gw) * bounds.width;
        const [r, g, b, a] = sampleGradient(spec, x, y);
        const sel = selection === null ? 255 : selection.valueAt(Math.round(x), Math.round(y));
        const at = (gy * gw + gx) * 4;
        image.data[at] = r;
        image.data[at + 1] = g;
        image.data[at + 2] = b;
        image.data[at + 3] = Math.round((a * sel) / 255);
      }
    }
    gridCtx.putImageData(image, 0, 0);

    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.globalCompositeOperation = COMPOSITE_OPERATION[blendMode];
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(grid, bounds.x, bounds.y, bounds.width, bounds.height);
    ctx.restore();
  }
}
